<template>
  <nav role="navigation" :aria-label="$t('ariaLabelPagination')">
    <ul class="flex items-center gap-3">
      <li>
        <a
          class="flex h-8 min-w-8 cursor-pointer items-center justify-center text-primary transition-all"
          :class="[
            internalValue === 1
              ? 'pointer-events-none opacity-0'
              : 'cursor-pointer hover:opacity-80',
          ]"
          :href="
            localePath({
              path: $route.path,
              query: {
                ...$route.query,
                page: internalValue - 1 || 1,
              },
            })
          "
          :aria-label="$t('pagePrev')"
          @click.prevent="internalValue--"
        >
          <UiIcon name="chevron_left" class="size-6" />
        </a>
      </li>
      <li v-for="(i, idx) in paginationRange" :key="idx">
        <component
          :is="i === 'dots' ? 'span' : 'a'"
          class="max-xs:px-0.5 flex h-8 min-w-8 items-center justify-center px-1.5 transition-all"
          :class="[
            i === internalValue
              ? 'bg-primary text-off-white-10'
              : 'bg-gray-60/10 text-primary',
            i === 'dots' ? 'cursor-default' : 'cursor-pointer hover:opacity-80',
          ]"
          v-bind="{
            ...(i !== 'dots' && {
              href: localePath({
                path: $route.path,
                query: {
                  ...$route.query,
                  page: i,
                },
              }),
              ariaLabel:
                i === internalValue
                  ? `${$t('currentPage')}, ${$t('page') + ' ' + i}`
                  : `${$t('ariaLabelGoTo', { title: $t('page') + ' ' + i })}`,
              ariaCurrent: i === internalValue,
            }),
          }"
          @click.prevent="
            i !== 'dots' && internalValue !== i ? (internalValue = i) : null
          "
        >
          <template v-if="i === 'dots'">...</template>
          <template v-else>{{ i }}</template>
        </component>
      </li>
      <li>
        <a
          class="flex h-10 min-w-11 items-center justify-center text-primary transition-all"
          :class="[
            internalValue === totalPages
              ? 'pointer-events-none opacity-0'
              : 'cursor-pointer hover:opacity-80',
          ]"
          :href="
            localePath({
              path: $route.path,
              query: {
                ...$route.query,
                page: nextPage,
              },
            })
          "
          :aria-label="$t('pageNext')"
          @click.prevent="internalValue++"
        >
          <UiIcon name="chevron_right" class="size-6" />
        </a>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
const props = defineProps<{
  modelValue: number;
  totalPages: number;
}>();
const { currentPage, paginationRange } = usePagination({
  siblings: 1,
  boundaries: 1,
  total: props.totalPages,
  initialPage: props.modelValue,
});
const localePath = useLocalePathPolyfill();

const emit = defineEmits(["update:modelValue"]);

watch(
  () => props.modelValue,
  () => {
    currentPage.value = props.modelValue;
  },
);

const internalValue = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    // emit("update:modelValue", newValue);
    currentPage.value = newValue;
    emit("update:modelValue", newValue);
  },
});

const nextPage = computed(() => {
  return internalValue.value + 1 > props.totalPages
    ? props.totalPages
    : internalValue.value + 1;
});
</script>

<style lang="css">
/* .page-pagination {
  @apply flex items-center;
}

.pagination-prev .page-link,
.pagination-next .page-link {
  @apply flex h-10 min-w-11 cursor-pointer items-center justify-center font-medium transition-all hover:opacity-80;
}
.pagination-page .page-link {
  @apply max-xs:px-0.5 flex h-10 min-w-10 cursor-pointer items-center justify-center bg-white px-1.5 font-medium text-primary transition-all hover:opacity-80;
}

.pagination-prev .page-link:active,
.pagination-next .page-link:active,
.pagination-page .page-link:active {
  @apply brightness-90;
}

.pagination-prev.disabled .page-link,
.pagination-next.disabled .page-link {
  @apply pointer-events-none opacity-50;
}

.pagination-prev.disabled .page-link:active,
.pagination-next.disabled .page-link:active {
  @apply filter-none;
}

.pagination-page.disabled .page-link {
  @apply pointer-events-none bg-transparent shadow-none;
}
.pagination-page.active .page-link {
  @apply bg-secondary text-white;
}

.pagination-page.disabled .page-link {
  cursor: default;
} */
</style>
